import React, { useState, useEffect, useCallback } from "react";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { toast, ToastContainer } from "react-toastify";
import { FaTimes } from "react-icons/fa";
import { useDropzone } from "react-dropzone";
import ClipLoader from "react-spinners/ClipLoader";
import "react-toastify/dist/ReactToastify.css";
import "../../index.css";
import api from "../../utils/api"; // Import the API utility
import log from "../../utils/logger"; // Import the logger

const UploadCard = () => {
  const [selectedOccasions, setSelectedOccasions] = useState(null);
  const [occasions, setOccasions] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [cardDesigns, setCardDesigns] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [uploadProgress, setUploadProgress] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const animatedComponents = makeAnimated();

  const COLORS = {
    primary: "#f86060",
    secondary: "#f51f1f",
    border: "#bbb",
  };

  const fetchOccasions = useCallback(async () => {
    try {
      const response = await api.get("/category/get-active-categories");
      if (response.data.code === 200) setOccasions(response.data.data);
    } catch (error) {
      log.error("Failed to fetch occasions:", error);
      toast.error("Failed to fetch occasions");
    }
  }, []);

  const fetchTags = useCallback(async () => {
    try {
      const response = await api.get("/tag/get-active-tags");
      if (response.data.code === 200) setTags(response.data.data);
    } catch (error) {
      log.error("Failed to fetch tags:", error);
      toast.error("Failed to fetch tags");
    }
  }, []);

  useEffect(() => {
    fetchOccasions();
    fetchTags();
  }, [fetchOccasions, fetchTags]);

  const handleOccasionChange = (selectedOption) =>
    setSelectedOccasions(selectedOption);
  const handleTagChange = (selectedOptions) => setSelectedTags(selectedOptions);
  const handleClick = (e) => {
    e.stopPropagation(); // Prevent any parent click handlers
    document.querySelector("#dropzone-file").click();
  };
  const onDrop = (acceptedFiles) => {
    const pngFiles = acceptedFiles.filter((file) => file.type === "image/png");
    setCardDesigns((prev) => [...prev, ...pngFiles]);
    setPreviewImages((prev) => [
      ...prev,
      ...pngFiles.map((file) => URL.createObjectURL(file)),
    ]);
    setUploadProgress((prev) => [
      ...prev,
      ...new Array(pngFiles.length).fill(0),
    ]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "image/png": [],
    },
    maxFiles: 50,
  });

  const handleRemoveImage = (index) => {
    setCardDesigns((prev) => prev.filter((_, i) => i !== index));
    setPreviewImages((prev) => prev.filter((_, i) => i !== index));
    setUploadProgress((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const uploadedLinks = await Promise.all(
        cardDesigns.map(async (file, index) => {
          const formData = new FormData();
          formData.append("image", file);

          const response = await api.post("/auth/upload-s3", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadProgress((prev) => {
                const newProgress = [...prev];
                newProgress[index] = percentCompleted;
                return newProgress;
              });
            },
          });

          if (response.data.code === 200) {
            return response.data.data.detail;
          } else {
            throw new Error("Failed to upload image");
          }
        })
      );

      await Promise.all(
        uploadedLinks.map(async (link) => {
          const response = await api.post("/card/add-card", {
            thumbnail: link,
            card: link,
            categories: selectedOccasions ? [selectedOccasions.value] : [],
            tags: selectedTags.map((tag) => tag.value),
          });

          if (response.data.code !== 200) {
            throw new Error("Failed to add card");
          }
        })
      );

      toast.success("Card designs submitted successfully!");
      handleReset();
    } catch (error) {
      log.error("Error during submission:", error);
      toast.error("Failed to submit card designs");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedOccasions(null);
    setSelectedTags([]);
    setCardDesigns([]);
    setPreviewImages([]);
    setUploadProgress([]);
  };

  const customStyles = {
    control: (base) => ({
      ...base,
      padding: "3px",
      borderRadius: "0px",
      border: `1px solid ${COLORS.border}`,
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: COLORS.primary,
      borderRadius: "0px",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "white",
      padding: "5px",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "white",
      ":hover": {
        backgroundColor: COLORS.primary,
        color: "white",
      },
      borderRadius: "0px",
    }),
    option: (base, { isFocused, isSelected }) => ({
      ...base,
      backgroundColor: isFocused
        ? COLORS.secondary
        : isSelected
        ? COLORS.secondary
        : null,
      color: isFocused || isSelected ? "white" : COLORS.secondary,
      ":active": {
        ...base[":active"],
        backgroundColor: isSelected ? COLORS.secondary : null,
      },
    }),
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto bg-white rounded-lg shadow-lg">
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <div className="mb-6">
        <label className="block mb-2 text-gray-700 font-semibold">
          Occasions
        </label>
        <Select
          styles={customStyles}
          className="my-3"
          closeMenuOnSelect={true}
          components={animatedComponents}
          options={occasions.map((occ) => ({
            value: occ._id,
            label: occ.name,
          }))}
          onChange={handleOccasionChange}
          value={selectedOccasions}
          // isMulti
        />
      </div>
      <div className="mb-6">
        <label className="block mb-2 text-gray-700 font-semibold">
          For whom
        </label>
        <Select
          styles={customStyles}
          className="my-3"
          closeMenuOnSelect={false}
          components={animatedComponents}
          isMulti
          options={tags.map((tag) => ({ value: tag._id, label: tag.name }))}
          onChange={handleTagChange}
          value={selectedTags}
        />
      </div>
      <div className="mb-6">
        <label className="block mb-2 text-gray-700 font-semibold">
          Upload Card Designs
        </label>
        <div
          className="flex items-center justify-center w-full"
          // onClick={handleClick}
        >
          <label
            htmlFor="file-upload"
            {...getRootProps()}
            className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
          >
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <svg
                className="w-8 h-8 mb-4 text-gray-500"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 20 16"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                />
              </svg>
              <p className="mb-2 text-sm text-gray-500">
                <span className="font-semibold">Click to upload</span> or drag
                and drop
              </p>
              <p className="text-xs text-gray-500">PNG only (5x7in)</p>
            </div>
            <input
              id="dropzone-file"
              {...getInputProps()}
              className="hidden"
            />
          </label>
        </div>
        <div className="flex flex-wrap mt-4 space-x-4">
          {previewImages.map((image, index) => (
            <div key={index} className="relative mb-4">
              <img
                src={image}
                alt="Preview"
                className="w-72 h-96 rounded-lg object-cover border border-gray-300"
              />
              <button
                type="button"
                onClick={() => handleRemoveImage(index)}
                className="absolute top-0 left-0 bg-primary text-white rounded-full p-1"
              >
                <FaTimes size={12} />
              </button>
              <div className="absolute bottom-0 left-0 w-full bg-white bg-opacity-75">
                <div
                  className="h-2 bg-primary"
                  style={{ width: `${uploadProgress[index]}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex space-x-4">
        <button
          onClick={handleSubmit}
          className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark"
          disabled={isLoading}
        >
          {isLoading ? <ClipLoader size={24} color={"#fff"} /> : "Upload Cards"}
        </button>
        <button
          onClick={handleReset}
          className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-700"
        >
          Reset
        </button>
      </div>
    </div>
  );
};

export default UploadCard;
